import { KnowledgeDocDto } from "@growth/dto";
import { KNOWLEDGE_LABELS, KnowledgeKind } from "@growth/schema";
import { Badge, Button, cn, Textarea } from "@growth/ui";
import { History, LoaderCircle, RotateCcw, Save } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { ErrorText, PageHeader } from "../components/page-header";
import { useActive } from "../lib/active";
import { apiErrorMessage, apiErrorStatus } from "../lib/api-error";
import { formatRelative } from "../lib/format";
import { useKnowledge, useRevisions, useSaveKnowledge } from "../lib/workspace";

/** What agents know about the active brand: one markdown document per topic, versioned. */
export const KnowledgePage = () => {
  const { brand } = useActive();
  const docs = useKnowledge();
  const [kind, setKind] = useState<KnowledgeKind>("PRODUCT");
  const current = docs.data?.find((doc) => doc.kind === kind);

  return (
    <section className="space-y-6">
      <PageHeader eyebrow={brand?.name ?? ""} title="Brand profile">
        Everything agents know about this brand. They read it on every run, so keep it true and
        current. Facts content may state live in Claims.
      </PageHeader>

      {docs.data?.some((doc) => doc.draftWaiting) && (
        <p className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm">
          The researcher drafted changes to{" "}
          {docs.data
            .filter((doc) => doc.draftWaiting)
            .map((doc) => KNOWLEDGE_LABELS[doc.kind].label)
            .join(", ")}
          . Agents keep using the versions below until someone confirms them.{" "}
          <Link to="/setup" className="font-medium text-accent underline">
            Review in Brand setup
          </Link>
        </p>
      )}

      {docs.isError && <ErrorText>{apiErrorMessage(docs.error)}</ErrorText>}
      {docs.isPending && (
        <LoaderCircle className="size-5 animate-spin text-muted" aria-label="Loading" />
      )}

      {docs.data && (
        <div className="grid gap-6 lg:grid-cols-[14rem_minmax(0,1fr)]">
          <nav aria-label="Documents" className="space-y-1">
            {docs.data.map((doc) => (
              <button
                key={doc.kind}
                type="button"
                onClick={() => setKind(doc.kind)}
                aria-current={doc.kind === kind ? "page" : undefined}
                className={cn(
                  "flex w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-left text-sm",
                  doc.kind === kind
                    ? "bg-white font-medium shadow-sm ring-1 ring-line"
                    : "text-muted hover:bg-white",
                )}
              >
                {KNOWLEDGE_LABELS[doc.kind].label}
                {doc.version === 0 && <span className="text-[10px] text-muted">empty</span>}
              </button>
            ))}
          </nav>
          {current && <DocEditor key={`${current.kind}:${current.version}`} doc={current} />}
        </div>
      )}
    </section>
  );
};

const DocEditor = ({ doc }: { doc: KnowledgeDocDto }) => {
  const { atLeast } = useActive();
  const canEdit = atLeast("EDITOR");
  const save = useSaveKnowledge();
  const [body, setBody] = useState(doc.body);
  const [showHistory, setShowHistory] = useState(false);
  const revisions = useRevisions(doc.kind, showHistory);
  const dirty = body !== doc.body;

  // Warn before leaving with unsaved changes.
  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const conflict = save.isError && apiErrorStatus(save.error) === 409;

  return (
    <div className="min-w-0 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-serif text-xl text-ink">{KNOWLEDGE_LABELS[doc.kind].label}</h2>
          <p className="text-sm text-muted">{KNOWLEDGE_LABELS[doc.kind].about}</p>
        </div>
        <div className="flex items-center gap-2">
          {doc.version > 0 && (
            <Badge>
              v{doc.version}
              {doc.updatedAt && ` · ${formatRelative(doc.updatedAt)}`}
              {doc.updatedBy && ` · ${doc.updatedBy.name ?? doc.updatedBy.email}`}
            </Badge>
          )}
          <Button
            variant="ghost"
            onClick={() => setShowHistory((open) => !open)}
            disabled={doc.version === 0}
          >
            <History className="size-4" />
            History
          </Button>
          {canEdit && (
            <Button
              onClick={() => save.mutate({ kind: doc.kind, body, baseVersion: doc.version })}
              disabled={!dirty || save.isPending}
            >
              {save.isPending ? (
                <LoaderCircle className="size-4 animate-spin" />
              ) : (
                <Save className="size-4" />
              )}
              Save
            </Button>
          )}
        </div>
      </div>

      {conflict && (
        <ErrorText>
          Someone saved this after you opened it. Copy your text somewhere safe, reload the page,
          and apply your changes again.
        </ErrorText>
      )}
      {save.isError && !conflict && <ErrorText>{apiErrorMessage(save.error)}</ErrorText>}

      <Textarea
        aria-label={`${KNOWLEDGE_LABELS[doc.kind].label} (markdown)`}
        value={body}
        onChange={(event) => setBody(event.target.value)}
        readOnly={!canEdit}
        rows={28}
        placeholder={
          canEdit ? "Write in markdown. Headings, lists and tables are fine." : "Nothing here yet."
        }
        className="font-mono text-[13px] leading-relaxed"
      />
      {!canEdit && (
        <p className="text-xs text-muted">You can read this. Editors and owners can change it.</p>
      )}

      {showHistory && (
        <div className="rounded-xl border border-line bg-white">
          <p className="border-b border-line px-4 py-2 text-xs font-semibold uppercase tracking-widest text-muted">
            Earlier versions
          </p>
          {revisions.isPending && <LoaderCircle className="m-4 size-4 animate-spin text-muted" />}
          <ul className="divide-y divide-line">
            {revisions.data?.map((revision) => (
              <li
                key={revision.version}
                className="flex items-center justify-between gap-3 px-4 py-2 text-sm"
              >
                <span>
                  v{revision.version} · {formatRelative(revision.createdAt)}
                  {revision.editedBy && ` · ${revision.editedBy.name ?? revision.editedBy.email}`}
                  {revision.editedBy === null && " · imported"}
                </span>
                {canEdit && revision.version !== doc.version && (
                  <Button variant="ghost" onClick={() => setBody(revision.body)}>
                    <RotateCcw className="size-4" />
                    Load into editor
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
